import { AlarmLevel } from '@/shared/types';
import { EchartsWithTheme, Empty, useTheme } from '@maxtropy/components';
import { apiV2AlarmRecordAnalysisAlarmLevelStatisticsPost } from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import { isNil } from 'lodash-es';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AlarmRecordsAnalysisContext } from '../..';
import { DateType } from '../../const';
import { getDefaultTime } from '../../utils';
import CardBox from '../CardBox';
import { getBarChartOption, getChartOption } from './charts';
import styles from './index.module.scss';

const AlarmLevelStatistics = () => {
  const theme = useTheme();
  const {
    timeResolution,
    value,
    selectedSceneId,
    activeKey,
    setAlarmModalOpen,
    setSearchParams,
    searchParams,
    sceneTabList,
  } = useContext(AlarmRecordsAnalysisContext);
  const [pieLegendData, setPieLegendData] = useState<any[]>();

  // 统计等级统计
  const { data: alarmLevelStatistics, loading } = useRequest(
    async () => {
      return await apiV2AlarmRecordAnalysisAlarmLevelStatisticsPost({
        sceneId: selectedSceneId,
        state: activeKey === 'all' ? undefined : activeKey,
        timeResolution: timeResolution === DateType.CUSTOM ? 'D' : timeResolution,
        startTime: getDefaultTime(timeResolution!, value?.[0]).startTime,
        endTime: getDefaultTime(timeResolution!, value?.[1]).endTime,
      });
    },
    {
      ready: !!timeResolution && !!value && !!activeKey,
      refreshDeps: [timeResolution, selectedSceneId, value, activeKey],
    }
  );

  useEffect(() => {
    setPieLegendData(
      [
        {
          name: '最高级',
          level: AlarmLevel.HIGHEST,
          value: alarmLevelStatistics?.topAlarmNum,
          color: '#FF4D4F',
        },
        {
          name: '高级',
          level: AlarmLevel.HIGH,
          value: alarmLevelStatistics?.highAlarmNum,
          color: '#FFCB47',
        },
        {
          name: '中级',
          level: AlarmLevel.MIDDLE,
          value: alarmLevelStatistics?.middleAlarmNum,
          color: '#2D8DFF',
        },
        {
          name: '低级',
          level: AlarmLevel.LOW,
          value: alarmLevelStatistics?.lowAlarmNum,
          color: '#16DD8E',
        },
        {
          name: '最低级',
          level: AlarmLevel.LOWEST,
          value: alarmLevelStatistics?.warningAlarmNum,
          color: '#ABD335',
        },
      ].filter(item => item?.value !== 0)
    );
  }, [alarmLevelStatistics]);

  // 图表
  const chartOption = useMemo(() => {
    return getChartOption(alarmLevelStatistics, pieLegendData);
  }, [alarmLevelStatistics, pieLegendData]);

  const barChartOption = useMemo(() => {
    return getBarChartOption(alarmLevelStatistics, timeResolution);
  }, [alarmLevelStatistics, timeResolution]);

  const isEmpty = useMemo(() => {
    return !alarmLevelStatistics || alarmLevelStatistics?.totalNum === 0;
  }, [alarmLevelStatistics]);

  return (
    <CardBox title="报警等级统计">
      {isEmpty ? (
        <Empty />
      ) : (
        <>
          <div className={styles.top_pie_wrapper}>
            <div className={styles.chart_wrapper}>
              <EchartsWithTheme
                empty={{
                  isShowEmpty: !alarmLevelStatistics,
                  emptyNode: <Empty />,
                }}
                loading={loading}
                type="pie"
                theme={theme}
                style={{ height: 180 }}
                option={chartOption}
              />
            </div>
            <div className={styles.customer_legend}>
              {(pieLegendData ?? []).map(item => (
                <div
                  className={styles.legend_item}
                  key={item.name}
                  onClick={() => {
                    setSearchParams?.({
                      ...searchParams,
                      levels: [item.level],
                    });
                    setAlarmModalOpen?.(true);
                  }}
                >
                  <div className={styles.icon} style={{ backgroundColor: item.color }}></div>
                  <div className={styles.label}>{item.name ?? '--'}</div>
                  <div className={styles.value}>{!isNil(item.value) ? item.value : '--'}</div>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.bottom_bar_wrapper}>
            <EchartsWithTheme
              empty={{
                isShowEmpty: !alarmLevelStatistics,
                emptyNode: <Empty />,
              }}
              loading={loading}
              theme={theme}
              style={{ height: '100%' }}
              option={barChartOption}
              onEvents={{
                click: (params: any) => {
                  setSearchParams?.({
                    ...searchParams,
                    startTime: params?.data?.startTime,
                    endTime: params?.data?.endTime,
                    deviceLabelIds: selectedSceneId ? [selectedSceneId] : (sceneTabList ?? []).map(i => i.id!),
                  });
                  setAlarmModalOpen?.(true);
                },
              }}
            />
          </div>
        </>
      )}
    </CardBox>
  );
};

export default AlarmLevelStatistics;
